import { defineComponent, h, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { ActivityLogs } from '@/ui/components/activityLogs/ActivityLogs';
import { t } from '@/ui/i18n/i18n';
import { useIssueActivities } from '@/ui/pages/issue/useIssueActivities';
import { useTask } from '@/ui/concerns';
import { listIssueActivities } from '@/services/api/resources/activity/actions';
import { ActivitiesList, ActivityCreated } from '@/entities/activity/model';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { createComment } from '@/services/api/resources/issue/v2';
import { emptyList } from '@/services/api';
import { ActivityQuery } from '@/services/api/resources/activity/query';
import { VNodeData } from 'vue/types/vnode.d';
import { IssueType } from '@/entities/issue';
import { LogIssueCreator } from '@/ui/components/activityLogs/factory/LogIssueCreator';

interface IssueLogsProps {
  issueId: string | number;
  approvalStatus: string | undefined;
}

export const IssueLogs = defineComponent<IssueLogsProps>({
  name: 'IssueEditPageLogs',
  props: ['issueId', 'approvalStatus'],
  setup(props) {
    const logs = ref<ActivitiesList>(emptyList());

    const createCommentTask = useTask(
      (comment: string | undefined) => createComment(props.issueId.toString(), comment as string),
      response => {
        logs.value.data.unshift(response.data);
      },
    );

    const onLogCommentCreated = (comment: string) => createCommentTask.run(comment);

    const { valuesTranslation, fieldsTranslation } = useIssueActivities();

    const isLoadingActivities = ref(false);

    const query = ref<ActivityQuery>({
      page: 1,
      perPage: 20,
    });

    const fetchActivities = useTask(
      () => listIssueActivities(props.issueId.toString(), query.value),
      response => {
        logs.value.meta = response.meta;
        logs.value.links = response.links;
        logs.value.data = [...logs.value.data, ...response.data];
        isLoadingActivities.value = false;
      },
    );

    const onSeeDetailsClick = (log: ActivityCreated) => {
      if (log.payload.type === IssueType.ManualForm) {
        EventBus.$emit(Events.seeMoreDetailsIssue);
      } else {
        EventBus.$emit(Events.seeMoreDetailsIssue, true);
      }
    };

    const hasMoreActivities = computed(() => logs.value.links.next !== null);

    const onScrollBottom = () => {
      if (!hasMoreActivities.value || isLoadingActivities.value) {
        return;
      }

      query.value.page += 1;
      fetchActivities.run();
    };

    const resetData = () => {
      logs.value.data = [];
    };

    watch(
      () => props.approvalStatus,
      newStatus => {
        if (newStatus) {
          resetData();
          fetchActivities.run();
        }
      },
      { immediate: true },
    );

    onBeforeUnmount(resetData);
    onMounted(fetchActivities.run);

    return function render() {
      return h('div', { class: 'issue-with-logs__activity-logs-loader' }, [
        h(ActivityLogs, {
          props: {
            valuesTranslation,
            fieldsTranslation,
            logs: logs.value.data,
            showCreateComment: true,
            resourceName: t('common.issue').toLowerCase(),
            componentCreator: new LogIssueCreator(),
          },
          on: {
            'comment:created': onLogCommentCreated,
            'see-details:click': onSeeDetailsClick,
            'scroll:bottom': onScrollBottom,
          },
        }),
      ]);
    };
  },
});

export function issueLogs(data: VNodeData & { props: IssueLogsProps }) {
  return h(IssueLogs, data);
}
